"use client";

import * as React from "react";
import type { Omittable } from "../../lib/props";
import { Box } from "./box";

/**
 * Static SVG markup drawn at a size, for artwork the app ships as strings
 * (a country flag, a vendor mark) rather than as components. The markup is
 * the consumer's own static asset, never user content: it is written into
 * the DOM as-is. Decorative unless `label` names it.
 */
export function InlineSvg({
  markup,
  width,
  height,
  label,
}: Omittable<{
  /** The `<svg …>…</svg>` string. Its own width and height attributes are
   *  replaced by `width` and `height`. */
  markup: string;
  width: number;
  height: number;
  /** The accessible name; omit for decoration. */
  label?: string;
}>): React.ReactElement {
  const ref = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    const node = ref.current;
    if (!node) return;
    node.innerHTML = markup.replace(
      /^<svg\b[^>]*>/,
      (tag) =>
        `${tag.replace(/\s(width|height)="[^"]*"/g, "")}`.replace(
          /^<svg\b/,
          `<svg width="${width}" height="${height}"`,
        ),
    );
  }, [markup, width, height]);
  return (
    <Box
      aria-hidden={label ? undefined : true}
      aria-label={label}
      data-slot="inline-svg"
      height={height}
      overflow="hidden"
      ref={ref}
      role={label ? "img" : undefined}
      shrink={false}
      width={width}
    />
  );
}
